import React, { useEffect } from 'react';
import './WorkWithChart.scss';
import { PmrPortfolio } from '../PmrPortfolio/PmrPortfolio';
import { useTranslation } from 'react-i18next';
import Project from '../../../models/Project';
import { PmrClassification } from '../../../models/PmrClassification';
import { PortfolioProjectsTable } from '../PortfolioProjectsTable/PortfolioProjectsTable';
import { ProjectCounter } from '../ProjectCounter/ProjectCounter';
import { useSelector, useDispatch } from 'react-redux';
import {
  getProjectsByDivisionPmr,
  getProjectsByDivision,
  getProjectsByDate,
} from '../../../redux/actions/indicatorProjects';
import Division from '../../../models/Division';
import { IndicatorKeyEnum as key } from '../../../models/IndicatorKeyEnum';
import DivisionsTableHeader from './DivisionsTableHeader/DivisionsTableHeader';
import {
  resetIndicatorDetailFilter,
  setIndicatorDetailFilterChart,
} from '../../../redux/actions/indicatorDetailFilters';
import { handleLoading } from '../../utils';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import Filter from '../../Portfolio/Filter/Filter';

export const getFilteredProjects = (
  d,
  pmrClassification,
  division,
  totalProjects,
  indicatorKey,
  indicatorId,
  completeGroupingValue,
  setLoading,
  projectTypeFilterChange?,
) => {
  switch (indicatorKey) {
    case key.PMR_PERFORMANCE_LAST_CLOSED_YEAR:
      return handleLoading(
        () =>
          d(getProjectsByDivisionPmr(pmrClassification, division, totalProjects, indicatorId, projectTypeFilterChange)),
        setLoading,
      );
    case key.PORTFOLIO_OVERVIEW_INCLUDING_REGIONAL_PROJECTS_BY_DIVISION:
      return completeGroupingValue !== ''
        ? handleLoading(
            () => d(getProjectsByDivision(completeGroupingValue, totalProjects, indicatorId, projectTypeFilterChange)),
            setLoading,
          )
        : null;
    default:
      return null;
  }
};

export const getFilterProjectsByDate = (d, dataDate, indicatorId) => {
  return d(getProjectsByDate(dataDate.month, dataDate.year, indicatorId));
};

export const getSelectedDivision = (divisionsArray, division, t, indicatorKey = '', completeGroupingValue = '') => {
  const divisionCode = division?.trim();
  const isSelectableDivision = divisionsArray.find(obj => obj.code === divisionCode);
  return isSelectableDivision
    ? isSelectableDivision
    : indicatorKey === key.PORTFOLIO_OVERVIEW_INCLUDING_REGIONAL_PROJECTS_BY_DIVISION
    ? {
        id: completeGroupingValue,
        name: divisionCode,
        code: completeGroupingValue,
      }
    : { id: 0, name: '', code: '' };
};

export const dispatchIndicatorFilterChart = (d, filterIndicators, selectedDivision, division, pmrClassification) => {
  d(
    setIndicatorDetailFilterChart({
      ...filterIndicators.indicatorCharts,
      chartTitle: selectedDivision ? selectedDivision.name : division,
      subTitle: pmrClassification,
    }),
  );
};

interface ReceivedProps {
  indicatorKey?: string;
  pmrClassification: PmrClassification;
  division?: string;
  totalProjects?: number;
  dataDate?: any;
  indicatorId?: number;
  disbursementValue?: number;
  completeGroupingValue?: string;
}
type Props = ReceivedProps;

enum indicatorType {
  PMR = 'PMR_PERFORMANCE_LAST_CLOSED_YEAR',
  POIR = 'PORTFOLIO_OVERVIEW_INCLUDING_REGIONAL_PROJECTS_BY_DIVISION',
}

export const WorkWithChart = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [havePmr, setHavePmr] = React.useState(true);
  const [loadingData, setLoadingData] = React.useState<boolean>(false);
  const indicatorProjects = useSelector<any, Project[]>(state => state.indicatorProjects);
  const filterIndicators = useSelector<any, any>(state => state.indicatorDetailFilter);
  const divisionsArray = useSelector<any, Division[]>(state => state.divisions);

  const {
    pmrClassification,
    division,
    totalProjects,
    indicatorKey,
    dataDate,
    indicatorId,
    disbursementValue,
    completeGroupingValue,
  } = props;

  const divisionText = 'division';
  const divisionSelected = divisionsArray
    ? getSelectedDivision(divisionsArray, division, t, indicatorKey, completeGroupingValue)
    : { name: '' };

  // Filter
  const filterIndicatorsBar = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;

  const projectTypeFilterChange = {
    ...filterIndicatorsBar,
    common: { ...filterIndicatorsBar.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async indicatorKey => {
    dispatch(setLoading(true));

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicatorsBar.common.projectTypeFilters,
        indicatorKey,
      );
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan], indicatorKey) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));

    if (indicatorKey === indicatorType.PMR) {
      dispatch(
        setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'PMR_PERFORMANCE_LAST_CLOSED_YEAR')),
      );
    } else if (indicatorKey === indicatorType.POIR) {
      dispatch(
        setCurrentIndicator(
          sendEndSummary?.find(
            data => data.indicatorKey === 'PORTFOLIO_OVERVIEW_INCLUDING_REGIONAL_PROJECTS_BY_DIVISION',
          ),
        ),
      );
    }
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    if (filterIndicators.indicatorCharts.chartTitle === '' && filterIndicators.indicatorCharts.firstTime) {
      dispatchIndicatorFilterChart(
        dispatch,
        filterIndicators,
        getSelectedDivision(divisionsArray, division, t),
        division,
        pmrClassification,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (divisionSelected !== undefined) {
      filterIndicators.indicatorCharts.firstTime &&
        dispatchIndicatorFilterChart(
          dispatch,
          filterIndicators,
          getSelectedDivision(divisionsArray, division, t),
          division,
          pmrClassification,
        );

      setHavePmr(true);

      filterIndicators.indicatorCharts.firstTime &&
        getFilteredProjects(
          dispatch,
          pmrClassification,
          divisionSelected.id,
          totalProjects,
          indicatorKey,
          indicatorId,
          completeGroupingValue,
          setLoadingData,
          projectTypeFilterChange,
        );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pmrClassification,
    division,
    dispatch,
    divisionsArray,
    totalProjects,
    indicatorKey,
    dataDate,
    indicatorId,
    filterIndicators.projectTypeFilterOnDetail,
    completeGroupingValue,
    filterIndicatorsBar.common,
  ]);

  useEffect(() => {
    if (
      filterIndicatorsBar.common.coReponsibleDivisions.length > 0 ||
      filterIndicatorsBar.common.countryId.length > 0 ||
      filterIndicatorsBar.common.divisionId.length > 0 ||
      filterIndicatorsBar.common.interestIssues.length > 0 ||
      filterIndicatorsBar.common.participantDivisions.length > 0 ||
      filterIndicatorsBar.common.regionId.length > 0 ||
      filterIndicatorsBar.common.sectors.length > 0 ||
      filterIndicatorsBar.common.teamLeaders.length > 0
    )
      handleCommonFilterChange(indicatorKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicatorsBar.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicatorsBar.common.coReponsibleDivisions.length === 0 &&
      filterIndicatorsBar.common.countryId.length === 0 &&
      filterIndicatorsBar.common.divisionId.length === 0 &&
      filterIndicatorsBar.common.interestIssues.length === 0 &&
      filterIndicatorsBar.common.participantDivisions.length === 0 &&
      filterIndicatorsBar.common.regionId.length === 0 &&
      filterIndicatorsBar.common.sectors.length === 0 &&
      filterIndicatorsBar.common.teamLeaders.length === 0
    ) {
      handleCommonFilterChange(indicatorKey);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicatorsBar.common]);

  return (
    <div className="work-with-chart-container">
      <>
        {renderTitleDescription(divisionText, indicatorKey, filterIndicators, disbursementValue, t, division)}
        {renderPmrClassification(indicatorKey, filterIndicators, t)}
        <div className="table-container">
          <PortfolioProjectsTable
            haveTcdi={indicatorKey === key.PORTFOLIO_OVERVIEW_INCLUDING_REGIONAL_PROJECTS_BY_DIVISION}
            havePmr={havePmr}
            projects={indicatorProjects}
            indicatorKey={indicatorKey}
          />
          <ProjectCounter arrayToCount={indicatorProjects} countProjects={true} />
        </div>
      </>

      {/* {indicatorProjects.length !== 0 && !isLoading ? (

      ) : (
        <ProjectsListSkeleton />
      )} */}
    </div>
  );
};

const renderTitleDescription = (divisionText, indicatorKey, filterIndicators, disbursementValue, t, division) => (
  <>
    <div className="title upper">
      <div className="left">{t(divisionText)} </div>
    </div>

    <div className="description">
      <div className="left">{filterIndicators.indicatorCharts.chartTitle} </div>
      {indicatorKey === key.PORTFOLIO_OVERVIEW_INCLUDING_REGIONAL_PROJECTS_BY_DIVISION ? (
        <DivisionsTableHeader division={division} />
      ) : null}
    </div>
  </>
);

const renderPmrClassification = (indicatorKey, filterIndicators, t) =>
  indicatorKey === key.PMR_PERFORMANCE_LAST_CLOSED_YEAR ? (
    <>
      <div className="title">{t('pmr_initials')}</div>
      <div className="pmr-classification">
        <PmrPortfolio pmrClassification={filterIndicators.indicatorCharts.subTitle} />
      </div>
    </>
  ) : null;
